import { validateScriptBlocks, validateScriptDocument } from './scriptPersistence';
import type { ScriptSnapshot } from './scriptPersistence';
import {validateRevisionReferences} from './scriptRevisions';

export interface RecoveryDraft {
    id: string;
    schema: 1;
    savedAt: string;
    baseRevision: number;
    snapshot: ScriptSnapshot;
    backupBeforeSave: boolean;
}

export function validateSnapshot(value: unknown): ScriptSnapshot {
    const snapshot = value as ScriptSnapshot | null;
    if (!snapshot || !snapshot.titlePage || typeof snapshot.titlePage !== 'object'
        || typeof snapshot.titlePage.title !== 'string' || typeof snapshot.titlePage.author !== 'string'
        || typeof snapshot.titlePage.contact !== 'string') throw new Error('Invalid recovery title page.');
    validateScriptBlocks(snapshot.blocks);
    if (snapshot.document !== undefined) validateScriptDocument(snapshot.document);
    validateRevisionReferences(snapshot.blocks,snapshot.document?.revisions);
    return snapshot;
}

// Each editor owns a separate entry: competing tabs cannot erase each other's drafts.
// The prefix also isolates signed-in accounts and projects on the same browser.
export class ScriptRecoveryJournal {
    private prefix: string;
    private key: string;
    private inheritedId?: string;
    constructor(private storage: Storage, accountId: string, projectId: string, writerId: string) {
        this.prefix = `ezart:story-recovery:v1:${encodeURIComponent(accountId)}:${encodeURIComponent(projectId)}:`;
        this.key = this.prefix + encodeURIComponent(writerId);
    }

    list(): { drafts: RecoveryDraft[]; unreadable: number } {
        const drafts: RecoveryDraft[] = [];
        let unreadable = 0;
        for (let i = 0; i < this.storage.length; i++) {
            const id = this.storage.key(i);
            if (!id?.startsWith(this.prefix)) continue;
            try {
                const value = JSON.parse(this.storage.getItem(id) || '') as RecoveryDraft;
                if (value.schema !== 1 || !Number.isSafeInteger(value.baseRevision) || value.baseRevision < 0
                    || typeof value.backupBeforeSave !== 'boolean' || !Number.isFinite(Date.parse(value.savedAt))) {
                    throw new Error('Invalid recovery entry.');
                }
                drafts.push({ ...value, id, snapshot: validateSnapshot(value.snapshot) });
            } catch { unreadable++; } // Keep unreadable entries; do not replace or silently delete them.
        }
        return { drafts: drafts.sort((a, b) => b.savedAt.localeCompare(a.savedAt)), unreadable };
    }

    inherit(id: string) { if (id.startsWith(this.prefix)) this.inheritedId = id; }

    write(snapshot: ScriptSnapshot, baseRevision: number, backupBeforeSave: boolean) {
        this.storage.setItem(this.key, JSON.stringify({
            schema: 1, savedAt: new Date().toISOString(), baseRevision, snapshot, backupBeforeSave,
        }));
    }

    acknowledge() {
        this.storage.removeItem(this.key);
        if (this.inheritedId) this.storage.removeItem(this.inheritedId);
        this.inheritedId = undefined;
    }
}
